import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { AuthImg } from "@/components/ShowcaseAdmin";
import { MicButton, appendText } from "@/components/MicButton";

const MAX = 3;

export const ShowcaseUploadDialog = ({ student, auth, onDone }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [city, setCity] = useState(student.city || "");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach(URL.revokeObjectURL), [previews]);

  const pick = (e) => {
    const next = [...files, ...Array.from(e.target.files || [])];
    if (next.length > MAX) toast.error(`Up to ${MAX} photos per project`);
    setFiles(next.slice(0, MAX));
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) return toast.error("Add at least one photo");
    const fd = new FormData();
    fd.append("enrollment_id", student.id);
    fd.append("title", title);
    fd.append("description", desc);
    fd.append("city", city);
    files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try {
      const { data } = await api.post("/teacher/showcase", fd, { headers: { ...auth.headers, "Content-Type": "multipart/form-data" } });
      if (data.ai?.flagged) toast.warning(`Uploaded, but please check the story: ${data.ai.reasons.join(", ")}. The admin will see this before it goes public.`, { duration: 8000 });
      else toast.success("Uploaded — waiting for admin approval");
      setOpen(false); setTitle(""); setDesc(""); setFiles([]); setCity(student.city || "");
      onDone?.();
    } catch (e2) {
      toast.error(e2.response?.data?.detail?.toString?.() || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-10 sm:h-8" data-testid={`showcase-upload-btn-${student.id}`}><Camera className="w-4 h-4 mr-1" />Photos</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="showcase-upload-dialog">
        <DialogHeader>
          <DialogTitle>Project photos · {student.student_name}</DialogTitle>
          <DialogDescription>Up to 3 photos (JPG, PNG or WEBP, max 8 MB each). Admin approves before they go public.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="flex gap-2 items-center">
            <Input required minLength={2} maxLength={120} placeholder="Project title, e.g. Smart dustbin" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="showcase-title-input" />
            <MicButton auth={auth} testid="showcase-title-mic" onText={(t) => setTitle((p) => appendText(p, t).slice(0, 120))} />
          </div>
          <div className="relative">
            <Textarea rows={3} maxLength={800} placeholder="What did the student build? Type or tap Speak (Hindi/English)" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="showcase-desc-input" className="pr-24" />
            <div className="absolute right-2 bottom-2"><MicButton auth={auth} testid="showcase-desc-mic" onText={(t) => setDesc((p) => appendText(p, t).slice(0, 800))} /></div>
          </div>
          <div>
            <Input maxLength={60} placeholder="City shown publicly, e.g. Patna" value={city} onChange={(e) => setCity(e.target.value)} data-testid="showcase-city-input" />
            <p className="text-xs text-slate-500 mt-1">Public cards show “Partner School{city.trim() ? `, ${city.trim()}` : ""}” — never the real school name.</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {previews.map((src, i) => (
              <div key={src} className="relative aspect-square rounded-lg overflow-hidden border">
                <img src={src} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5" data-testid={`showcase-remove-${i}`}><X className="w-3 h-3" /></button>
              </div>
            ))}
            {files.length < MAX && (
              <label className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-xs text-slate-500 cursor-pointer hover:border-[#FF2E63]" data-testid="showcase-file-label">
                <Camera className="w-5 h-5 mb-1" />Add photo
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={pick} data-testid="showcase-file-input" />
              </label>
            )}
          </div>
          <Button type="submit" disabled={busy} className="rs-cta rounded-full w-full" data-testid="showcase-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Upload ${files.length || ""} photo${files.length === 1 ? "" : "s"}`}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const STATUS = { pending: "bg-amber-500/15 text-amber-700", approved: "bg-emerald-500/15 text-emerald-700", rejected: "bg-red-500/15 text-red-600" };
export const StatusPill = ({ s, testid }) => <span className={`px-2 py-0.5 rounded-full text-xs font-bold capitalize ${STATUS[s]}`} data-testid={testid}>{s}</span>;

export const TeacherUploads = ({ items, token }) => (
  <div className="mt-8 bg-white rounded-2xl border border-slate-200" data-testid="teacher-uploads">
    <div className="p-5 border-b border-slate-200">
      <h2 className="font-bold text-lg text-[#0A192F]">My project uploads</h2>
      <p className="text-xs text-slate-500">Approved projects appear in the public gallery and the Parent Portal.</p>
    </div>
    {items.length === 0 ? <p className="p-8 text-center text-slate-500 text-sm" data-testid="teacher-uploads-empty">No uploads yet — use “Photos” next to a student.</p> : (
      <ul className="divide-y">
        {items.map((it) => (
          <li key={it.id} className="p-4 flex gap-3 items-center" data-testid={`teacher-upload-${it.id}`}>
            <AuthImg path={`/api/showcase/photos/${it.id}/0`} token={token} className="w-14 h-14 rounded-lg object-cover bg-white block" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#0A192F] truncate">{it.title}</div>
              <div className="text-xs text-slate-500">{it.student_name} · {it.photos.length} photo(s) · {new Date(it.created_at).toLocaleDateString("en-IN")}</div>
            </div>
            <StatusPill s={it.status} testid={`teacher-upload-status-${it.id}`} />
          </li>
        ))}
      </ul>
    )}
  </div>
);
